import { Button, Col, Form, Row } from "antd";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { CloseOutlined } from "@ant-design/icons";
import usePost from "../../../global/hooks/usePost";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { openErrorNotification } from "../../../global/utils/toast";

export const TempUserForm = ({
  setIsModalOpen,
  selectedData,
  onSuccess,
  isModalOpen,
}) => {
  const [form] = Form.useForm();
  const tempUserUrl = `${process.env.REACT_APP_API_URL}/api/sites/temp-user/`;
  const processedResponseRef = useRef(null);

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(tempUserUrl);

  // Reset form when modal opens
  useEffect(() => {
    if (isModalOpen) {
      form.resetFields();
      processedResponseRef.current = null;
    }
  }, [isModalOpen, form]);

  const onFinish = (values) => {
    const modifiedValues = {
      ...values,
      SiteNos: selectedData?.map((item) => item.DeviceNo),
      StartTime: dayjs(values.StartTime).format("YYYY-MM-DD HH:mm"),
      EndTime: dayjs(values.EndTime).format("YYYY-MM-DD HH:mm"),
    };
    // Reset processed ref when making new request
    processedResponseRef.current = null;
    postAction(modifiedValues);
  };

  // Handle response - pass to parent for summary modal display
  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong"
      );
      processedResponseRef.current = null;
    } else if (!postLoading && !postError && postData) {
      // Use ref to prevent processing the same response multiple times
      const responseKey = JSON.stringify(postData);
      if (processedResponseRef.current === responseKey) {
        return; // Already processed
      }
      processedResponseRef.current = responseKey;

      // Close form modal and pass response to parent
      setIsModalOpen(false);
      if (onSuccess) {
        onSuccess(postData);
      }
    }
  }, [postError, postData, postLoading, setIsModalOpen, onSuccess]);
  return (
    <Form
      validateTrigger={"onChange"}
      form={form}
      onFinish={onFinish}
      scrollToFirstError
      layout={"vertical"}
      autoComplete={"off"}
    >
      <div>
        <div className="bg-white rounded-[4px] overflow-hidden">
          <div className="px-2 pt-1 pb-3">
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <CustomInput label="Username" name="UserId" required />
              </Col>
              <Col span={24}>
                <CustomInput
                  label="Password"
                  name="Password"
                  autoComplete="Password"
                  type="password"
                  required
                />
              </Col>
              <Col span={24}>
                <CustomDateTimePicker
                  label="Start Time"
                  name="StartTime"
                  placeholder="Start Time"
                  format="YYYY-MM-DD HH:mm" // 24-hour format
                  required
                />
              </Col>
              <Col span={24}>
                <CustomDateTimePicker
                  label="End Time"
                  name="EndTime"
                  placeholder="End Time"
                  format="YYYY-MM-DD HH:mm" // 24-hour format
                  required
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const startTime = getFieldValue("StartTime");
                        if (!value || !startTime) {
                          return Promise.resolve();
                        }
                        if (dayjs(value).isAfter(dayjs(startTime))) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("End time must be later than start time")
                        );
                      },
                    }),
                  ]}
                />
              </Col>
            </Row>
          </div>
          <div className="flex justify-center space-x-2 mt-2.5">
            <Button
              type="primary"
              className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center "
              onClick={() => form.submit()}
              loading={postLoading}
            >
              <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
              <span className="">Apply</span>
            </Button>
            <Button
              className="reset-button"
              onClick={() => {
                form.resetFields();
                setIsModalOpen(false);
              }}
            >
              <CloseOutlined />
              <span>Cancel</span>
            </Button>
          </div>
        </div>
      </div>
    </Form>
  );
};
